// PURE FX clock (M10): the time base of every board tween, particle and idle animation (chute chevrons, the
// stall pulse, the Smelter glow …). It advances by the wall-clock frame interval, clamped (a background
// return never fast-forwards a burst), and can be frozen for a deterministic mid-animation screenshot
// (`__gm.render.freezeAt(ms)`): the call ARMS the freeze; the next frame holds the clock still (so the
// events queued in the same task as the call start exactly at that clock time) and sets the stop point
// `ms` later; the clock then runs up to it and stays there. `freezeAt(null)` resumes. Idle animations
// (chevrons, pulses, spins) read `idle` = the time since the last armed freeze: their phase at a freeze
// point depends only on `ms`, never on how long the page ran before (deterministic screenshots).

export class FxClock {
  private t = 0;
  private lastWall: number | null = null;
  private stopAt: number | null = null;
  private armed: number | null = null;
  private epoch = 0;
  private readonly maxStepMs: number;

  constructor(maxStepMs: number) {
    this.maxStepMs = maxStepMs;
  }

  /** FX time now (ms). */
  get now(): number {
    return this.t;
  }

  /** The idle-animation time: FX time since the last armed freeze (0 at boot). */
  get idle(): number {
    return this.t - this.epoch;
  }

  /** Advances by the wall interval since the last call (clamped; never past a freeze point). */
  advance(wallMs: number): number {
    const dt =
      this.lastWall === null ? 0 : Math.max(0, Math.min(this.maxStepMs, wallMs - this.lastWall));
    this.lastWall = wallMs;
    if (this.armed !== null) {
      // the frame after freezeAt(ms): hold still, stop `ms` from here
      this.stopAt = this.t + this.armed;
      this.epoch = this.t;
      this.armed = null;
      return this.t;
    }
    let next = this.t + dt;
    if (this.stopAt !== null) next = Math.min(next, this.stopAt);
    this.t = next;
    return this.t;
  }

  /** Re-bases the wall clock without advancing (a loop that slept while nothing animated wakes up). */
  resync(wallMs: number): void {
    this.lastWall = wallMs;
  }

  /** Arms a freeze `ms` of FX time after the next frame (0 = at it); null resumes. */
  freezeAt(ms: number | null): void {
    if (ms === null) {
      this.stopAt = null;
      this.armed = null;
      return;
    }
    this.armed = Math.max(0, ms);
  }

  /** A freeze is armed or set (the clock stops, or has stopped). */
  get freezing(): boolean {
    return this.armed !== null || this.stopAt !== null;
  }

  /** The clock reached its freeze point. */
  get frozen(): boolean {
    return this.armed === null && this.stopAt !== null && this.t >= this.stopAt;
  }
}
